<template>
  <div class="tw-contents">
    <ShortlistListsSelector
      v-if="enableShortlistInSearch && isLoggedInStudent"
      ref="shortlistListsSelector"
      :location="shortlistListLocation"
      @selection-change="handleListSelectionChange"
      @load-error="handleListSelectionError"
    />

    <div
      v-if="
        isLoadingShortlist ||
        (enableShortlistInSearch && isLoggedInStudent && isLoadingListSelection)
      "
      class="tw-w-full"
    >
      <PropertyCardsSkeleton />
    </div>

    <div v-else-if="sortedShortlisted.length" class="tw-contents">
      <div class="col-12 tw-relative">
        <div
          class="md:tw-justify-[unset] tw-relative tw-mb-[19px] tw-flex tw-items-center tw-justify-end tw-gap-2 md:tw-absolute md:-tw-top-[52px] md:tw-mb-[unset] md:tw-flex-row-reverse lg:-tw-top-[42px] lg:tw-right-0 lg:tw-flex-row lg:tw-pr-[22px]"
        >
          <div class="tw-absolute tw-left-0 tw-text-[#3B4143] md:tw-hidden">
            {{ studentStore.selectedCount }} selected
          </div>

          <span class="tw-mr-[4px] tw-text-[16px] tw-text-[#3B4143]">
            <span v-if="allSelected">Deselect all</span>
            <span v-else>Select all</span>
            <span class="tw-hidden md:tw-inline lg:tw-hidden">
              ({{ studentStore.selectedCount }} selected)
            </span>
          </span>

          <label
            class="tw-relative tw-inline-flex tw-cursor-pointer tw-items-center tw-rounded-full focus-within:tw-ring-2 focus-within:tw-ring-[#61B3E5] focus-within:tw-ring-offset-2"
          >
            <input
              type="checkbox"
              class="tw-sr-only"
              :checked="allSelected"
              :disabled="!sortedShortlisted.length || reloadingId !== null"
              @change="toggleSelectAll"
            />

            <div
              class="tw-h-[24px] tw-w-[46px] tw-rounded-full tw-border tw-transition-colors tw-duration-300"
              :class="
                allSelected
                  ? 'tw-border-[#00A2E1] tw-bg-[#00A2E1]'
                  : 'tw-border-[#3B4143] tw-bg-white'
              "
            ></div>

            <div
              class="tw-absolute tw-left-[3px] tw-top-[3px] tw-h-[18px] tw-w-[18px] tw-transform tw-rounded-full tw-transition-transform tw-duration-300"
              :class="
                allSelected
                  ? 'tw-translate-x-[22px] tw-bg-white'
                  : 'tw-translate-x-0 tw-bg-[#D9D9D9]'
              "
            ></div>
          </label>
        </div>
      </div>

      <div
        v-for="(property, index) in paginatedShortlisted"
        :key="shortlistPropertyKey(property, index)"
        class="col-12 col-sm-6 col-xl-4 property-listing-column tw-mb-[24px]"
        :data-id="property.id"
      >
        <PropertyListingFeatured
          v-if="property.isFeatured"
          :iteration="index + 1"
          :property="property"
          :enable-housemate-groups="enableHousemateGroups"
          :show-housemate-group-tab="showHousemateGroupTab"
          :admin="admin"
          :admin-first-name="adminFirstName"
          :bottom-of-the-page="bottomOfThePage"
          :current-scroll="currentScroll"
          :window-height="windowHeight"
          :screen-key="screenKey"
          :map-is-open="mapIsOpen"
          :shortlist-nudge-enabled="shortlistNudgeEnabled"
          :is-shortlist-view="true"
          :shortlist-read-only="isActiveShortlistReadOnly"
          :reloading="reloadingId === property.id"
          :is-selected="selectedPropertyIds.includes(String(property.id))"
          @remove-from-shortlist="handleRemoveFromShortlist"
          @selection-change="onSelectionChange"
        />

        <PropertyListingBackup2024
          v-else
          :iteration="index + 1"
          :property="property"
          :enable-housemate-groups="enableHousemateGroups"
          :enable-updated-property-tile="enableUpdatedPropertyTile"
          :show-housemate-group-tab="showHousemateGroupTab"
          :admin="admin"
          :admin-first-name="adminFirstName"
          :bottom-of-the-page="bottomOfThePage"
          :current-scroll="currentScroll"
          :window-height="windowHeight"
          :screen-key="screenKey"
          :map-is-open="mapIsOpen"
          :shortlist-nudge-enabled="shortlistNudgeEnabled"
          :is-shortlist-view="true"
          :shortlist-read-only="isActiveShortlistReadOnly"
          :reloading="reloadingId === property.id"
          :is-selected="selectedPropertyIds.includes(String(property.id))"
          @remove-from-shortlist="handleRemoveFromShortlist"
          @selection-change="onSelectionChange"
        />
      </div>

      <div class="col-12">
        <div
          id="sticky-container"
          class="tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-50 tw-flex tw-w-full tw-items-center tw-justify-between tw-border-t tw-border-[#D1D5DB] tw-bg-white tw-p-[16px] tw-transition-all tw-duration-300 tw-ease-out md:tw-sticky md:tw-inset-x-auto md:tw-bottom-[24px] md:tw-z-10 md:tw-mx-auto md:tw-mb-[44px] md:tw-w-full md:tw-max-w-[586px] md:tw-rounded-full md:tw-border-gray-200 md:tw-shadow-lg"
          :class="{
            'md:tw-pointer-events-auto md:tw-translate-y-0 md:tw-opacity-100':
              isStickyActionsVisible,
            'md:tw-pointer-events-none md:tw-translate-y-[80px] md:tw-opacity-0':
              !isStickyActionsVisible,
          }"
        >
          <div class="tw-flex tw-w-full tw-items-center tw-gap-[16px]">
            <div class="tw-relative">
              <button
                type="button"
                class="tw-flex tw-h-[50px] tw-w-[50px] tw-items-center tw-justify-center tw-rounded-[25px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-p-[13px] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-border-uhblue-700 focus-visible:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
                @click="showMenuOptions = !showMenuOptions"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 10.5625C12.7939 10.5625 13.4375 11.2061 13.4375 12C13.4375 12.7939 12.7939 13.4375 12 13.4375C11.2061 13.4375 10.5625 12.7939 10.5625 12C10.5625 11.2061 11.2061 10.5625 12 10.5625Z"
                    fill="#00A2E1"
                    stroke="#00A2E1"
                    stroke-width="0.125"
                  />
                  <path
                    d="M5.625 10.5625C6.41891 10.5625 7.0625 11.2061 7.0625 12C7.0625 12.7939 6.41891 13.4375 5.625 13.4375C4.83109 13.4375 4.1875 12.7939 4.1875 12C4.1875 11.2061 4.83109 10.5625 5.625 10.5625Z"
                    fill="#00A2E1"
                    stroke="#00A2E1"
                    stroke-width="0.125"
                  />
                  <path
                    d="M18.375 10.5625C19.1689 10.5625 19.8125 11.2061 19.8125 12C19.8125 12.7939 19.1689 13.4375 18.375 13.4375C17.5811 13.4375 16.9375 12.7939 16.9375 12C16.9375 11.2061 17.5811 10.5625 18.375 10.5625Z"
                    fill="#00A2E1"
                    stroke="#00A2E1"
                    stroke-width="0.125"
                  />
                </svg>
              </button>

              <ShortlistFloatingMenuOptions
                :show-menu-options="showMenuOptions"
                :share-url="shortlistShareUrl"
                :resolve-share-url="resolveShortlistShareUrl"
                :show-share="canShowShortlistShare"
                :selected-count="selectedCount"
                :is-logged-in-student="isLoggedInStudent"
                :enable-housemate-groups="enableHousemateGroups"
                :show-housemate-group-tab="showHousemateGroupTab"
                @open-context="openContext"
                @close-floating-menu-options="showMenuOptions = false"
                @move-to-group="handleMoveToGroup"
                @share-shortlist="handleShareShortlist"
                @remove-selected="handleRemoveSelected"
              />
            </div>

            <div class="tw-flex tw-h-[50px] tw-flex-1 tw-gap-4">
              <button
                type="button"
                class="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-[8px] tw-self-stretch tw-rounded-[30px] tw-bg-[#F48831] tw-px-[24px] tw-py-[8px] hover:tw-bg-[#E16B0C] focus-visible:tw-bg-[#E16B0C] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-bg-[#efefef] disabled:hover:tw-bg-[#efefef]"
                @click="handleEnquireClick"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M15.75 3.375H2.25C2.10082 3.375 1.95774 3.43426 1.85225 3.53975C1.74676 3.64524 1.6875 3.78832 1.6875 3.9375V13.5C1.6875 13.7984 1.80603 14.0845 2.017 14.2955C2.22798 14.5065 2.51413 14.625 2.8125 14.625H15.1875C15.4859 14.625 15.772 14.5065 15.983 14.2955C16.194 14.0845 16.3125 13.7984 16.3125 13.5V3.9375C16.3125 3.78832 16.2532 3.64524 16.1477 3.53975C16.0423 3.43426 15.8992 3.375 15.75 3.375ZM9 9.36211L3.69633 4.5H14.3037L9 9.36211ZM6.94055 9L2.8125 12.7835V5.21648L6.94055 9ZM7.77305 9.76289L8.6168 10.5398C8.72057 10.6351 8.85632 10.688 8.99719 10.688C9.13806 10.688 9.2738 10.6351 9.37758 10.5398L10.2213 9.76289L14.2995 13.5H3.69633L7.77305 9.76289ZM11.0595 9L15.1875 5.21578V12.7842L11.0595 9Z"
                    fill="white"
                  />
                </svg>
                <span
                  class="tw-font-inter tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%] tw-text-white"
                >
                  Enquire to
                  <span v-if="selectedCount > 0">{{ selectedCount }}</span>
                  <span v-else>all</span>
                  {{ selectedCount === 1 ? 'property' : 'properties' }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <div class="tw-h-[82px] md:tw-hidden"></div>
      </div>

      <SaveToShortlistModal
        v-if="enableHousemateGroups"
        :open="showSaveToShortlistModal"
        :property-ids="bulkPropertyIds"
        :show-personal-shortlist-option="false"
        :allow-set-as-default="false"
        :title="bulkModalTitle"
        :initial-mode="bulkModalInitialMode"
        :show-share-step="bulkActionMode === 'share_shortlist'"
        :is-logged-in-student="isLoggedInStudent"
        @close="closeSaveToShortlistModal"
        @saved="handleBulkSavedToGroup"
      />

      <HousemateGroupModal
        v-if="enableHousemateGroups && showPostMergeRemoveModal"
        :show-close-icon="true"
        @close-modal="closePostMergeRemoveModal"
      >
        <template #header>
          <span>Remove from personal shortlist?</span>
        </template>

        <template #body>
          <p
            class="tw-font-inter tw-mb-0 tw-text-[14px] tw-leading-[150%] tw-text-[#6B7280]"
          >
            {{ postMergeRemoveCopy }}
          </p>
        </template>

        <template #actions>
          <button
            type="button"
            class="tw-font-inter tw-flex tw-h-[50px] tw-flex-1 tw-items-center tw-justify-center tw-rounded-[30px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[24px] tw-py-[8px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
            :disabled="postMergeRemoving"
            @click="closePostMergeRemoveModal"
          >
            Keep in shortlist
          </button>

          <button
            type="button"
            class="tw-font-inter tw-flex tw-h-[50px] tw-flex-1 tw-items-center tw-justify-center tw-rounded-[30px] tw-bg-[#00A2E1] tw-px-[24px] tw-py-[8px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-white hover:tw-bg-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
            :disabled="postMergeRemoving"
            @click="confirmPostMergeRemove"
          >
            <span v-if="postMergeRemoving">Removing…</span>
            <span v-else>Remove from shortlist</span>
          </button>
        </template>
      </HousemateGroupModal>
    </div>

    <div
      v-else
      class="tw-flex tw-w-full tw-items-center tw-justify-center tw-px-[16px] tw-pb-[120px] tw-pt-[16px]"
    >
      <div
        class="tw-flex tw-w-full tw-max-w-[400px] tw-flex-col tw-items-center"
      >
        <div class="tw-aspect-[400/273] tw-w-full tw-max-w-[400px]">
          <img
            :src="cdnAsset('svg/unihomes-device-screens-new.svg')"
            alt=""
            loading="lazy"
            decoding="async"
            class="tw-block tw-h-full tw-w-full tw-object-contain"
          />
        </div>

        <h2
          class="tw-my-[8px] tw-p-0 tw-text-center tw-text-[16px] tw-font-[600] tw-leading-[24px] tw-text-[#3B4143]"
        >
          Nothing in your shortlist, yet!
        </h2>

        <p
          class="tw-mb-[16px] tw-p-0 tw-text-center tw-text-[14px] tw-font-[400] tw-leading-[21px] tw-text-[#6B7280]"
        >
          Add homes to your shortlist for a quick and easy way to share them
          with friends.
        </p>

        <p
          class="tw-mb-[24px] tw-p-0 tw-text-center tw-text-[14px] tw-font-[400] tw-leading-[21px] tw-text-[#6B7280]"
        >
          When you are ready, enquire about all of them at once with a single
          message.
        </p>

        <button
          type="button"
          class="tw-flex tw-min-h-[50px] tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-bg-uhblue-500 tw-px-6 tw-py-2 tw-text-[16px] tw-font-[600] tw-leading-[21px] tw-text-white hover:tw-bg-uhblue-700 focus-visible:tw-bg-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
          @click="studentStore.handleReturnToSearchPage()"
        >
          <MagnifyingGlass /> Back to search results
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useStudentStore } from '@js/stores/student/student';
import api from '@js/api/authenticatedApi';

import PropertyListingBackup2024 from '@components/PropertyListingBackup2024.vue';
import PropertyListingFeatured from '@components/PropertyListingFeatured.vue';

import MagnifyingGlass from '@components/Student/SVG/MagnifyingGlass.vue';
import ShortlistFloatingMenuOptions from '@components/Student/Menu/ShortlistFloatingMenuOptions.vue';
import SaveToShortlistModal from '@components/Student/Modal/SaveToShortlistModal.vue';
import HousemateGroupModal from '@components/Student/Modal/HousemateGroupModal.vue';
import PropertyCardsSkeleton from '@components/Student/PropertyCardsSkeleton.vue';
import ShortlistListsSelector from '@components/Student/ShortlistListsSelector.vue';
import {
  buildShortlistFilters,
  getFilteredShortlist,
} from '@components/Student/shortlistFilters';

import shortlistPageRestore from '@components/Student/helpers/shortlistPageRestore';

const RECENTLY_ADDED_HASH = 'recently-added';

export default {
  name: 'UserShortlists',
  components: {
    ShortlistListsSelector,
    PropertyListingBackup2024,
    PropertyListingFeatured,
    MagnifyingGlass,
    ShortlistFloatingMenuOptions,
    SaveToShortlistModal,
    HousemateGroupModal,
    PropertyCardsSkeleton,
  },
  mixins: [shortlistPageRestore],
  props: {
    enableShortlistInSearch: {
      type: Boolean,
      default: false,
    },
    currentCityName: {
      type: String,
      default: null,
    },
    currentAreaName: {
      type: String,
      default: null,
    },
    enableHousemateGroups: {
      type: Boolean,
      default: false,
    },
    enableUpdatedPropertyTile: {
      type: Boolean,
      default: false,
    },
    showHousemateGroupTab: {
      type: Boolean,
      default: true,
    },
    isLoggedInStudent: {
      type: Boolean,
      default: false,
    },
    admin: {
      default: false,
      type: Boolean,
    },
    adminFirstName: {
      default: null,
      type: String,
    },
    bottomOfThePage: {
      default: window.innerHeight,
      type: Number,
    },
    currentScroll: {
      default: 0,
      type: Number,
    },
    windowHeight: {
      default: window.innerHeight,
      type: Number,
    },
    screenKey: {
      default: null,
      type: String,
    },
    mapIsOpen: {
      default: false,
      type: Boolean,
    },
    shortlistNudgeEnabled: {
      default: false,
      type: Boolean,
    },
    selectAll: {
      type: Boolean,
      default: false,
    },
    currentBedrooms: {
      default: null,
      type: [Number, String],
    },
    currentBathrooms: {
      default: null,
      type: [Number, String],
    },
    currentMinPrice: {
      default: null,
      type: [Number, String],
    },
    currentMaxPrice: {
      default: null,
      type: [Number, String],
    },
    minPriceSelected: {
      default: false,
      type: Boolean,
    },
    maxPriceSelected: {
      default: false,
      type: Boolean,
    },
    currentType: {
      default: null,
      type: [String, Array],
    },
    currentLandlord: {
      default: null,
      type: [Object, String],
    },
    useExactRoomCounts: {
      default: false,
      type: Boolean,
    },
    sortKey: {
      default: 'relevance',
      type: String,
    },
    page: {
      default: 1,
      type: [Number, String],
    },
    paginate: {
      default: 0,
      type: Number,
    },
  },
  data() {
    return {
      studentStore: useStudentStore(),
      isLoadingShortlist: false,
      isLoadingListSelection: true,
      activeShortlistList: null,
      activeShortlistProperties: [],
      isActiveShortlistReadOnly: false,
      showMenuOptions: false,
      showSaveToShortlistModal: false,
      bulkPropertyIds: [],
      bulkActionMode: null,
      showPostMergeRemoveModal: false,
      postMergePropertyIds: [],
      postMergeGroupId: null,
      postMergeRemoving: false,
      shortlistShareUrl: null,
      reloadingId: null,
      isStickyActionsVisible: true,
      scrollTimeout: null,
      lastScrollY: 0,
      scrollDelta: 0,
      SCROLL_THRESHOLD: 80,
      isPreparingEnquiry: false,
      enquirySubmitForms: [],
      enquirySubmitHandler: null,
      pageShowHandler: null,
    };
  },
  computed: {
    shortlistListLocation() {
      return {
        city_name: this.currentCityName,
        area_name: this.currentAreaName,
      };
    },
    shortlisted() {
      const filters = this.studentStore.disableFilters
        ? {}
        : buildShortlistFilters({
            currentBedrooms: this.currentBedrooms,
            currentBathrooms: this.currentBathrooms,
            currentMinPrice: this.currentMinPrice,
            currentMaxPrice: this.currentMaxPrice,
            minPriceSelected: this.minPriceSelected,
            maxPriceSelected: this.maxPriceSelected,
            currentType: this.currentType,
            currentLandlord: this.currentLandlord,
            useExactRoomCounts: this.useExactRoomCounts,
          });

      if (this.enableShortlistInSearch && this.activeShortlistList) {
        return this.studentStore.filterProperties(
          this.activeShortlistProperties,
          filters
        );
      }

      return getFilteredShortlist({ store: this.studentStore, filters });
    },
    shortlistPageOffset() {
      const perPage = Number(this.paginate || 0);
      if (perPage <= 0) {
        return 0;
      }

      const currentPage = Math.max(1, Number(this.page) || 1);
      return (currentPage - 1) * perPage;
    },
    paginatedShortlisted() {
      if (!Array.isArray(this.sortedShortlisted)) {
        return [];
      }

      const perPage = Number(this.paginate || 0);
      if (perPage <= 0) {
        return this.sortedShortlisted;
      }

      const currentPage = Math.max(1, Number(this.page) || 1);
      const start = (currentPage - 1) * perPage;

      return this.sortedShortlisted.slice(start, start + perPage);
    },
    sortedShortlisted() {
      const list = Array.isArray(this.shortlisted) ? [...this.shortlisted] : [];
      const sortKey = String(this.sortKey || 'relevance');

      if (sortKey === 'price-asc') {
        return list.sort((a, b) => this.getPrice(a) - this.getPrice(b));
      }

      if (sortKey === 'price-desc') {
        return list.sort((a, b) => this.getPrice(b) - this.getPrice(a));
      }

      return list;
    },
    selectedPropertyIds() {
      return this.studentStore.selectedPropertyIds;
    },
    isRecentlyAddedList() {
      return this.activeShortlistList?.hash_id === RECENTLY_ADDED_HASH;
    },
    allSelected() {
      if (!this.enableShortlistInSearch || !this.activeShortlistList) {
        return this.studentStore.allSelected;
      }

      return (
        this.sortedShortlisted.length > 0 &&
        this.sortedShortlisted.every((property) =>
          this.selectedPropertyIds.includes(String(property.id))
        )
      );
    },
    selectedCount() {
      return this.studentStore.selectedCount;
    },
    canShowShortlistShare() {
      if (
        this.enableShortlistInSearch &&
        this.activeShortlistList &&
        this.activeShortlistList?.name !== 'Personal shortlist'
      ) {
        return false;
      }

      return this.studentStore.shortlistedPropertiesCount > 0;
    },
    bulkModalTitle() {
      if (this.bulkActionMode === 'share_shortlist') {
        return 'Share shortlist';
      }

      return this.bulkActionMode === 'move_all'
        ? 'Move to group'
        : 'Merge into group';
    },
    bulkModalInitialMode() {
      return this.bulkActionMode === 'share_shortlist' ? 'create' : 'choose';
    },
    postMergeRemoveCopy() {
      const count = Array.isArray(this.postMergePropertyIds)
        ? this.postMergePropertyIds.length
        : 0;

      const groups = Array.isArray(this.studentStore.housemateGroups)
        ? this.studentStore.housemateGroups
        : [];
      const groupName = groups.find(
        (group) =>
          Number(group?.housemate_group?.id) === Number(this.postMergeGroupId)
      )?.housemate_group?.name;

      const groupSuffix = groupName
        ? ` in “${groupName}”`
        : ' in the grouplist';

      if (count === 1) {
        return `This property has been added to your grouplist. Would you also like to remove it from your personal shortlist? It will stay${groupSuffix}.`;
      }

      return `These properties have been added to your grouplist. Would you also like to remove them from your personal shortlist? They will stay${groupSuffix}.`;
    },
  },
  watch: {
    canShowShortlistShare(canShowShare) {
      if (!canShowShare) {
        this.shortlistShareUrl = null;
      }
    },
  },
  async mounted() {
    if (typeof this.studentStore.invalidateShortlist === 'function') {
      this.studentStore.invalidateShortlist();
    }

    this.isLoadingShortlist = true;

    try {
      await this.studentStore.getShortlist();
    } finally {
      this.isLoadingShortlist = false;
    }

    window.addEventListener('scroll', this.handleScroll, { passive: true });

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('open-enquiry-modal') === '1') {
      this.$nextTick(() => {
        const isHousemateContextRequested =
          searchParams.get('enquiry-context') === 'housemate_group';
        const hasExistingHousemateScope =
          this.hasExplicitHousemateEnquiryScope();

        if (
          this.enableHousemateGroups &&
          (isHousemateContextRequested || hasExistingHousemateScope)
        ) {
          if (isHousemateContextRequested) {
            this.applyEnquiryContextFromQuery(searchParams);
          }
          this.handleEnquireClick({ preserveExistingContext: true });
          return;
        }

        this.handleEnquireClick();
      });
    }

    this.$nextTick(() => {
      this.rehydrateHousemateEnquiryModalSummary();
      this.setEnquiryModalSummary();
    });

    if (typeof document !== 'undefined') {
      this.enquirySubmitHandler = () => {
        this.setEnquiryModalSummary();
      };

      this.enquirySubmitForms = Array.from(
        document.querySelectorAll('form[data-form="booking"]')
      ).filter((form) => {
        const action = form.getAttribute('action') || '';

        return action.includes('/student/shortlist/book-viewings');
      });

      this.enquirySubmitForms.forEach((form) => {
        form.addEventListener('submit', this.enquirySubmitHandler);
      });
    }

    this.pageShowHandler = () => {
      this.$nextTick(() => {
        this.rehydrateHousemateEnquiryModalSummary();
        this.setEnquiryModalSummary();
      });
    };

    window.addEventListener('pageshow', this.pageShowHandler);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);

    if (this.pageShowHandler) {
      window.removeEventListener('pageshow', this.pageShowHandler);
    }

    if (this.enquirySubmitHandler && this.enquirySubmitForms.length) {
      this.enquirySubmitForms.forEach((form) => {
        form.removeEventListener('submit', this.enquirySubmitHandler);
      });
    }
  },
  methods: {
    handleListSelectionChange({ list, properties, readOnly }) {
      this.activeShortlistList = list;
      this.activeShortlistProperties = Array.isArray(properties)
        ? properties
        : [];
      if (this.isRecentlyAddedList) {
        this.studentStore.setShortlistCount(
          this.activeShortlistProperties.length
        );
      }
      this.isActiveShortlistReadOnly = Boolean(readOnly);
      this.isLoadingListSelection = false;
      this.studentStore.clearShortlistSelection();
      this.shortlistShareUrl = null;
    },
    handleListSelectionError() {
      this.isLoadingListSelection = false;
      this.activeShortlistList = null;
      this.activeShortlistProperties = [];
      this.isActiveShortlistReadOnly = false;
    },
    onShortlistRestored() {
      this.$nextTick(() => {
        this.rehydrateHousemateEnquiryModalSummary();
        this.setEnquiryModalSummary();
      });
    },
    hasExplicitHousemateEnquiryScope() {
      if (typeof document === 'undefined') {
        return false;
      }

      const contextInput = document.getElementById('enquiry_context');
      const propertyIdsInput = document.getElementById(
        'enquiry_housemate_group_property_ids'
      );

      return (
        contextInput instanceof HTMLInputElement &&
        contextInput.value === 'housemate_group' &&
        propertyIdsInput instanceof HTMLInputElement &&
        propertyIdsInput.value.trim().length > 0
      );
    },
    rehydrateHousemateEnquiryModalSummary() {
      if (typeof document === 'undefined') {
        return;
      }

      const enquiryModalToggle = document.getElementById('enquiry_modal');
      const contextInput = document.getElementById('enquiry_context');
      const propertyIdsInput = document.getElementById(
        'enquiry_housemate_group_property_ids'
      );

      const hasOpenModal =
        enquiryModalToggle instanceof HTMLInputElement &&
        enquiryModalToggle.checked;
      const isHousemateContext =
        contextInput instanceof HTMLInputElement &&
        contextInput.value === 'housemate_group';
      const hasPropertyScope =
        propertyIdsInput instanceof HTMLInputElement &&
        propertyIdsInput.value.trim().length > 0;

      if (!hasOpenModal || !isHousemateContext || !hasPropertyScope) {
        return;
      }

      this.setEnquiryModalSummary();
    },
    getPrice(property) {
      const candidates = [
        property?.cheapestRoomPrice,
        property?.price,
        property?.cheapest_room_price,
      ];

      for (const candidate of candidates) {
        const value = Number(candidate);
        if (Number.isFinite(value)) {
          return value;
        }
      }

      return Number.MAX_SAFE_INTEGER;
    },
    shortlistPropertyKey(property, index) {
      const propertyId = property?.id;

      if (
        propertyId !== null &&
        propertyId !== undefined &&
        propertyId !== ''
      ) {
        return `shortlist-property-${propertyId}`;
      }

      return `shortlist-property-fallback-${this.shortlistPageOffset + index}`;
    },
    closeSaveToShortlistModal() {
      this.showSaveToShortlistModal = false;
      this.bulkPropertyIds = [];
      this.bulkActionMode = null;
    },
    closePostMergeRemoveModal() {
      if (this.postMergeRemoving) {
        return;
      }

      this.showPostMergeRemoveModal = false;
      this.postMergePropertyIds = [];
      this.postMergeGroupId = null;
    },
    async confirmPostMergeRemove() {
      if (!this.enableHousemateGroups) {
        return;
      }

      if (this.postMergeRemoving) {
        return;
      }

      const ids = Array.isArray(this.postMergePropertyIds)
        ? this.postMergePropertyIds
        : [];

      if (!ids.length) {
        this.closePostMergeRemoveModal();
        return;
      }

      this.postMergeRemoving = true;
      try {
        await this.removePropertiesFromShortlist(ids);
      } finally {
        this.postMergeRemoving = false;
        this.closePostMergeRemoveModal();
      }
    },
    handleMoveToGroup({ mode } = {}) {
      if (!this.enableHousemateGroups) {
        return;
      }

      const actionMode =
        mode === 'merge_selected' ? 'merge_selected' : 'move_all';

      const ids =
        actionMode === 'merge_selected'
          ? Array.isArray(this.selectedPropertyIds)
            ? this.selectedPropertyIds
            : []
          : Array.isArray(this.sortedShortlisted)
          ? this.sortedShortlisted
              .map((property) => property?.id)
              .filter(Boolean)
          : [];

      if (!ids.length) {
        return;
      }

      this.bulkActionMode = actionMode;
      this.bulkPropertyIds = ids;
      this.showSaveToShortlistModal = true;
    },
    handleShareShortlist() {
      if (!this.isLoggedInStudent) {
        this.showMenuOptions = false;
        window.location.href = '/login';
        return;
      }

      if (!this.enableHousemateGroups) {
        return;
      }

      const ids = Array.isArray(this.sortedShortlisted)
        ? this.sortedShortlisted.map((property) => property?.id).filter(Boolean)
        : [];

      if (!ids.length) {
        return;
      }

      this.bulkActionMode = 'share_shortlist';
      this.bulkPropertyIds = ids;
      this.showSaveToShortlistModal = true;
    },
    async handleBulkSavedToGroup({ destination, groupId } = {}) {
      if (!this.enableHousemateGroups) {
        return;
      }

      if (destination !== 'group' || !groupId) {
        return;
      }

      if (this.bulkActionMode === 'share_shortlist') {
        this.studentStore.clearSelection();
        return;
      }

      // For "Move all to group" we also remove from personal shortlist.
      if (this.bulkActionMode === 'move_all') {
        const ids = Array.isArray(this.bulkPropertyIds)
          ? this.bulkPropertyIds
          : [];

        if (ids.length) {
          try {
            await this.removePropertiesFromShortlist(ids);
          } finally {
            this.studentStore.clearSelection();
          }
        }
      } else {
        // After a merge, keep the shortlist, clear selection, and offer to
        // remove from personal shortlist.
        this.postMergePropertyIds = Array.isArray(this.bulkPropertyIds)
          ? [...this.bulkPropertyIds]
          : [];
        this.postMergeGroupId = Number(groupId);
        this.studentStore.clearSelection();
        this.showPostMergeRemoveModal = true;
      }

      this.closeSaveToShortlistModal();
    },
    async handleRemoveFromShortlist(propertyId) {
      if (!propertyId) {
        return;
      }

      if (this.enableShortlistInSearch && this.activeShortlistList) {
        if (this.isActiveShortlistReadOnly) {
          return;
        }

        this.reloadingId = propertyId;
        try {
          if (this.isRecentlyAddedList) {
            this.removeFromActiveList([propertyId]);

            try {
              await this.studentStore.removeFromShortlist(propertyId);
            } finally {
              await this.reloadRecentlyAddedSelection();
            }
          } else {
            await this.$refs.shortlistListsSelector.removeProperty(propertyId);
          }
        } catch (error) {
          const sentry = window.Sentry;
          if (sentry && typeof sentry.captureException === 'function') {
            sentry.captureException(error);
          }
        } finally {
          this.reloadingId = null;
        }

        return;
      }

      this.reloadingId = propertyId;

      try {
        await this.studentStore.removeFromShortlist(propertyId);
        this.studentStore.invalidateShortlist();
        await this.studentStore.getShortlist();
      } catch (error) {
        const sentry = window.Sentry;
        if (sentry && typeof sentry.captureException === 'function') {
          sentry.captureException(error);
        }
      } finally {
        this.reloadingId = null;
      }
    },
    async handleRemoveSelected() {
      await this.removePropertiesFromShortlist(this.selectedPropertyIds);
    },
    async removePropertiesFromShortlist(propertyIds) {
      if (this.isRecentlyAddedList) {
        this.removeFromActiveList(propertyIds);

        try {
          await this.studentStore.removeMultipleFromShortlist(propertyIds);
        } finally {
          await this.reloadRecentlyAddedSelection();
        }
      } else {
        await this.studentStore.removeMultipleFromShortlist(propertyIds);
      }
    },
    removeFromActiveList(propertyIds) {
      const idsToRemove = new Set(
        (Array.isArray(propertyIds) ? propertyIds : []).map((id) => String(id))
      );
      this.activeShortlistProperties = this.activeShortlistProperties.filter(
        (property) => !idsToRemove.has(String(property?.id))
      );
      if (this.isRecentlyAddedList) {
        this.studentStore.setShortlistCount(this.activeShortlistProperties.length);
      }
    },
    async reloadRecentlyAddedSelection() {
      const response = await api.get(
        '/account/shortlist-lists/recently-added/properties'
      );
      this.activeShortlistProperties = response.data.shortlisted || [];
      this.studentStore.setShortlistCount(
        this.activeShortlistProperties.length
      );
    },
    openContext(context) {
      if (context === 'change_group') {
        this.showMenuOptions = false;
        this.showGroupListModal = true;
      }

      if (context === 'create_group') {
        this.showMenuOptions = false;
        this.showGroupCreateModal = true;
      }
    },
    onSelectionChange({ id, checked }) {
      if (checked) {
        this.studentStore.selectShortlistProperty(id);
      } else {
        this.studentStore.deselectShortlistProperty(id);
      }
    },
    toggleSelectAll(event) {
      if (this.enableShortlistInSearch && this.activeShortlistList) {
        if (event.target.checked) {
          this.sortedShortlisted.forEach((property) => {
            this.studentStore.selectShortlistProperty(property.id);
          });
        } else {
          this.studentStore.clearShortlistSelection();
        }

        return;
      }

      this.studentStore.toggleSelectAllShortlists(event.target.checked);
    },
    async resolveShortlistShareUrl() {
      if (this.shortlistShareUrl) {
        return this.shortlistShareUrl;
      }

      try {
        const shareUrl = await this.studentStore.createShortlistShareLink();
        this.shortlistShareUrl = shareUrl;

        return shareUrl;
      } catch (error) {
        if (error?.response?.status === 403) {
          throw {
            code: 'FORBIDDEN_SHARE',
          };
        }

        throw error;
      }
    },
    setEnquiryModalSummary() {
      if (typeof document === 'undefined') {
        return 0;
      }

      const countEl = document.getElementById(
        'enquiry_multi_property_count_text'
      );
      const locationRowEl = document.getElementById(
        'enquiry_multi_location_row'
      );
      const citiesEl = document.getElementById('enquiry_multi_cities_text');
      const lowestPriceEl = document.getElementById(
        'enquiry_multi_lowest_price_text'
      );
      const carouselEl = document.querySelector(
        '[data-uh="enquiry-modal-carousel"]'
      );
      const shortlistPropertyIdsInput = document.getElementById(
        'enquiry_shortlist_property_ids'
      );

      if (
        !countEl &&
        !locationRowEl &&
        !citiesEl &&
        !lowestPriceEl &&
        !carouselEl
      ) {
        return 0;
      }

      const selectedIds = Array.isArray(this.selectedPropertyIds)
        ? this.selectedPropertyIds.map(String)
        : [];

      const restoredShortlistIds =
        shortlistPropertyIdsInput instanceof HTMLInputElement
          ? shortlistPropertyIdsInput.value
              .split(',')
              .map((id) => String(id || '').trim())
              .filter(Boolean)
          : [];

      const contextInput = document.getElementById('enquiry_context');
      const propertyIdsInput = document.getElementById(
        'enquiry_housemate_group_property_ids'
      );

      const isHousemateContext =
        contextInput instanceof HTMLInputElement &&
        contextInput.value === 'housemate_group';

      const contextPropertyIds =
        isHousemateContext && propertyIdsInput instanceof HTMLInputElement
          ? propertyIdsInput.value
              .split(',')
              .map((id) => String(id || '').trim())
              .filter(Boolean)
          : [];

      const sourceProperties = Array.isArray(this.sortedShortlisted)
        ? this.sortedShortlisted
        : [];

      const activePropertyIds = contextPropertyIds.length
        ? contextPropertyIds
        : selectedIds.length
        ? selectedIds
        : restoredShortlistIds;

      const scopedEnquiryProperties = activePropertyIds.length
        ? sourceProperties.filter((property) =>
            activePropertyIds.includes(String(property?.id))
          )
        : sourceProperties;

      const hasExplicitHousemateScope =
        isHousemateContext && contextPropertyIds.length > 0;

      const enquiryProperties = hasExplicitHousemateScope
        ? scopedEnquiryProperties
        : scopedEnquiryProperties.length
        ? scopedEnquiryProperties
        : sourceProperties;

      if (shortlistPropertyIdsInput instanceof HTMLInputElement) {
        shortlistPropertyIdsInput.value =
          !hasExplicitHousemateScope && activePropertyIds.length
            ? activePropertyIds.join(',')
            : '';
      }

      const enquiryCount = enquiryProperties.length;

      const lowestPrice = enquiryProperties.reduce((carry, property) => {
        const price = this.getPrice(property);

        if (!Number.isFinite(price) || price === Number.MAX_SAFE_INTEGER) {
          return carry;
        }

        if (!Number.isFinite(carry)) {
          return price;
        }

        return Math.min(carry, price);
      }, NaN);

      const citySummary = enquiryProperties
        .map((property) => this.getPropertyCityLabel(property))
        .map((city) => String(city || '').trim())
        .filter((city, index, arr) => city && arr.indexOf(city) === index)
        .join(', ');

      const firstProperty = enquiryProperties[0] || null;
      const singlePropertyLocation = firstProperty
        ? String(
            firstProperty?.addressShort ||
              firstProperty?.address ||
              firstProperty?.fullAddress ||
              [
                firstProperty?.area?.name,
                firstProperty?.area?.city?.name,
                firstProperty?.city,
              ]
                .filter(Boolean)
                .join(', ')
          ).trim()
        : '';

      const locationText =
        enquiryCount === 1 ? singlePropertyLocation : citySummary;

      if (countEl) {
        countEl.textContent = enquiryCount
          ? `${enquiryCount} ${enquiryCount === 1 ? 'Property' : 'Properties'}`
          : '';
      }

      if (citiesEl) {
        citiesEl.textContent = locationText;
      }

      if (locationRowEl) {
        const isHousemateGroupsEnabled =
          contextInput instanceof HTMLInputElement;
        const shouldShowLocation =
          !!locationText && !(isHousemateGroupsEnabled && enquiryCount > 1);
        locationRowEl.classList.toggle('tw-hidden', !shouldShowLocation);
      }

      if (lowestPriceEl) {
        lowestPriceEl.textContent = Number.isFinite(lowestPrice)
          ? String(Math.round(lowestPrice))
          : '';
      }

      this.setEnquiryModalThumbnails(enquiryProperties);
      this.renderEnquiryModalCarousel(enquiryProperties);

      return enquiryCount;
    },
    renderEnquiryModalCarousel(enquiryProperties = []) {
      // Only present when the global enquiry modal is rendered.
      if (
        typeof window === 'undefined' ||
        !window.EnquiryModalCarousel ||
        typeof window.EnquiryModalCarousel.render !== 'function'
      ) {
        return;
      }

      const cards = enquiryProperties.map((property) => {
        const price = this.getPrice(property);
        const hasPrice =
          Number.isFinite(price) && price !== Number.MAX_SAFE_INTEGER;
        const mostExpensive = Number(property?.mostExpensiveRoomPrice);
        const address = String(
          property?.addressShort || property?.address || ''
        ).trim();

        return {
          url: property?.path || '#',
          thumbnail: this.getEnquiryThumbnail(property),
          alt: address,
          address,
          beds: property?.roomsAvailableCount ?? '',
          baths: property?.bathroomCount ?? '',
          from: Number.isFinite(mostExpensive)
            ? hasPrice && price !== mostExpensive
            : Boolean(property?.showFrom),
          price: hasPrice ? price : '',
        };
      });

      window.EnquiryModalCarousel.render(cards);
    },
    getPropertyCityLabel(property) {
      if (!property) {
        return '';
      }

      const explicitCity =
        property?.city ||
        property?.cityName ||
        property?.area?.city?.name ||
        property?.areaCityName ||
        '';

      if (String(explicitCity || '').trim()) {
        return String(explicitCity).trim();
      }

      const parsedCityFromPath = this.getCityFromPropertyPath(property?.path);

      if (parsedCityFromPath) {
        return parsedCityFromPath;
      }

      return '';
    },
    getCityFromPropertyPath(path) {
      if (!path) {
        return '';
      }

      const pathValue = String(path || '').trim();
      if (!pathValue) {
        return '';
      }

      try {
        const parsed = new URL(pathValue, window.location.origin);
        const segments = parsed.pathname.split('/').filter(Boolean);
        const citySlugIndex = segments.indexOf('student-accommodation');

        if (citySlugIndex === -1 || !segments[citySlugIndex + 1]) {
          return '';
        }

        const citySlug = segments[citySlugIndex + 1];
        return citySlug
          .split('-')
          .map((part) =>
            part.length ? part.charAt(0).toUpperCase() + part.slice(1) : ''
          )
          .join(' ')
          .trim();
      } catch (_error) {
        return '';
      }
    },
    getEnquiryThumbnail(property) {
      if (!property) {
        return null;
      }

      return (
        (typeof property?.mainImage === 'string' ? property.mainImage : null) ||
        property?.mainImage?.src?.medium?.url ||
        property?.mainImage?.src?.small?.url ||
        property?.mainImage?.src?.tablet?.url ||
        property?.main_image_tablet_url ||
        property?.mainImageTabletUrl ||
        property?.defaultImage ||
        null
      );
    },
    setEnquiryModalThumbnails(enquiryProperties = []) {
      if (typeof document === 'undefined') {
        return;
      }

      const thumbnailItems = Array.from(
        document.querySelectorAll('[data-enquiry-thumbnail-item]')
      );

      const thumbnailImages = Array.from(
        document.querySelectorAll('[data-enquiry-thumbnail-image]')
      );

      const singleThumbnail = document.getElementById(
        'enquiry_multi_single_thumbnail'
      );

      const thumbnails = enquiryProperties
        .map((property) => this.getEnquiryThumbnail(property))
        .filter(Boolean);

      if (thumbnailItems.length > 0) {
        thumbnailItems.forEach((item, index) => {
          const image = thumbnailImages[index];
          const thumbnail = thumbnails[index] || null;
          const shouldShow = index < enquiryProperties.length;

          if (shouldShow) {
            item.classList.remove('tw-hidden');

            if (image && thumbnail) {
              image.setAttribute('src', thumbnail);
            }

            return;
          }

          item.classList.add('tw-hidden');
        });
      }

      if (singleThumbnail && thumbnails[0]) {
        singleThumbnail.setAttribute('src', thumbnails[0]);
      }
    },
    applyEnquiryContextFromQuery(searchParams) {
      if (!this.enableHousemateGroups) {
        return;
      }

      if (typeof document === 'undefined') {
        return;
      }

      const contextInput = document.getElementById('enquiry_context');
      const groupIdInput = document.getElementById(
        'enquiry_housemate_group_id'
      );
      const propertyIdsInput = document.getElementById(
        'enquiry_housemate_group_property_ids'
      );

      const context = searchParams.get('enquiry-context') || 'shortlist';
      const groupId = searchParams.get('housemate-group-id') || '';
      const propertyIds =
        searchParams.get('housemate-group-property-ids') || '';

      if (contextInput instanceof HTMLInputElement) {
        contextInput.value = context;
      }

      if (groupIdInput instanceof HTMLInputElement) {
        groupIdInput.value = groupId;
      }

      if (propertyIdsInput instanceof HTMLInputElement) {
        propertyIdsInput.value = propertyIds;
      }
    },
    async handleEnquireClick({ preserveExistingContext = false } = {}) {
      if (this.isPreparingEnquiry) {
        return;
      }

      this.isPreparingEnquiry = true;

      try {
        if (typeof this.studentStore.getShortlist === 'function') {
          await this.studentStore.getShortlist();
        }

        await this.$nextTick();

        this.showMenuOptions = false;

        const hasExistingHousemateScope =
          this.hasExplicitHousemateEnquiryScope();

        let enquiryCount = this.setEnquiryModalSummary();

        if (
          enquiryCount === 0 &&
          typeof this.studentStore.invalidateShortlist === 'function' &&
          typeof this.studentStore.getShortlist === 'function'
        ) {
          this.studentStore.invalidateShortlist();
          await this.studentStore.getShortlist();
          await this.$nextTick();
          enquiryCount = this.setEnquiryModalSummary();
        }

        if (typeof document !== 'undefined') {
          const contextInput = document.getElementById('enquiry_context');
          const propertyIdsInput = document.getElementById(
            'enquiry_housemate_group_property_ids'
          );

          const hasExplicitHousemateScope =
            contextInput instanceof HTMLInputElement &&
            contextInput.value === 'housemate_group' &&
            propertyIdsInput instanceof HTMLInputElement &&
            propertyIdsInput.value.trim().length > 0;

          if (hasExplicitHousemateScope && enquiryCount === 0) {
            return;
          }
        }

        if (
          !preserveExistingContext &&
          !hasExistingHousemateScope &&
          typeof document !== 'undefined'
        ) {
          const contextInput = document.getElementById('enquiry_context');
          const groupIdInput = document.getElementById(
            'enquiry_housemate_group_id'
          );
          const propertyIdsInput = document.getElementById(
            'enquiry_housemate_group_property_ids'
          );

          if (contextInput instanceof HTMLInputElement) {
            contextInput.value = 'shortlist';
          }

          if (groupIdInput instanceof HTMLInputElement) {
            groupIdInput.value = '';
          }

          if (propertyIdsInput instanceof HTMLInputElement) {
            propertyIdsInput.value = '';
          }
        }

        if (typeof document !== 'undefined') {
          const enquiryModalToggle = document.getElementById('enquiry_modal');

          if (
            enquiryModalToggle &&
            enquiryModalToggle instanceof HTMLInputElement &&
            enquiryModalToggle.type === 'checkbox'
          ) {
            enquiryModalToggle.checked = true;
            return;
          }
        }

        if (typeof window !== 'undefined') {
          window.location.assign('/student/shortlist?open-enquiry-modal=1');
        }
      } finally {
        this.isPreparingEnquiry = false;
      }
    },
  },
};
</script>
